import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  date,
  index,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { studentRepertoire } from "./repertoire.js";
import { organisations, practiceTodos, students, users } from "./schema.js";

export const studentGoalStatusEnum = pgEnum("student_goal_status", [
  "active",
  "achieved",
  "paused",
  "abandoned",
]);

export type StudentGoalStatus = (typeof studentGoalStatusEnum.enumValues)[number];

/** A human-written, longer-lived teaching objective. */
export const studentGoals = pgTable(
  "student_goals",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "restrict" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    repertoireId: varchar("repertoire_id").references(() => studentRepertoire.id, {
      onDelete: "set null",
    }),
    title: text("title").notNull(),
    description: text("description"),
    status: studentGoalStatusEnum("status").notNull().default("active"),
    targetDate: date("target_date"),
    achievedAt: timestamp("achieved_at", { withTimezone: true }),
    /** Staff-only completion context; family reads must never return it. */
    completionNote: text("completion_note"),
    familyVisible: boolean("family_visible").notNull().default(false),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("student_goals_student_status_idx").on(
      t.organisationId,
      t.studentId,
      t.status,
      t.updatedAt,
    ),
    index("student_goals_repertoire_idx").on(t.organisationId, t.repertoireId),
    check(
      "student_goals_achieved_at_check",
      sql`(${t.status} = 'achieved' and ${t.achievedAt} is not null) or (${t.status} <> 'achieved' and ${t.achievedAt} is null)`,
    ),
  ],
);

/**
 * Optional goal → practice-task linkage. The practice table predates goals, so
 * the relationship stays separate and task completion cannot mutate goal state.
 */
export const studentGoalPracticeTodos = pgTable(
  "student_goal_practice_todos",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "restrict" }),
    goalId: varchar("goal_id")
      .notNull()
      .references(() => studentGoals.id, { onDelete: "cascade" }),
    practiceTodoId: varchar("practice_todo_id")
      .notNull()
      .references(() => practiceTodos.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("student_goal_practice_todos_task_idx").on(t.practiceTodoId),
    index("student_goal_practice_todos_goal_idx").on(t.organisationId, t.goalId, t.createdAt),
  ],
);
